import React, { useState } from 'react';
import { IconButton, Skeleton, Tooltip } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { PALETTE, DEFAULT_STATUS_PALETTE, getPaletteColors } from '../../../consts/AppointmentStatusColors';
import StepForm from './StepForm';
import ColorPicker from './ColorPicker';
import styles from '../AppointmentStepsPage.module.css';

/** Ação compacta da linha: ícone com tooltip (o span mantém o tooltip no botão desligado). */
function RowAction({ title, disabled, onClick, Icon, tone }) {
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size="small"
                    className={`${styles.rowAction} ${tone ? styles[tone] : ''}`}
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={title}
                >
                    <Icon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}

const RequiredTag = ({ required }) => (
    <span className={`${styles.tag} ${required ? styles.tagRequired : styles.tagOptional}`}>
        {required ? 'Obrigatória' : 'Opcional'}
    </span>
);

/** Bolinha da cor própria da etapa (sem cor, a etapa segue a do status) */
const StepColorDot = ({ colorKey }) => {
    const colors = getPaletteColors(colorKey);
    if (!colors) return null;
    return <span className={styles.stepColor} style={{ background: colors.borderColor }} title={colors.label} />;
};

/**
 * Um status e as etapas dele: cor do status, lista ordenada (subir, descer,
 * editar na linha, desativar), as desativadas com "Exibir inativas" e o campo
 * de nova etapa no fim.
 */
function StepStatusCard({ status, loading, steps, inactiveSteps, statusColor, canManage, busy, onSave, onMove, onDeactivate, onRestore, onStatusColor }) {
    const [editingId, setEditingId] = useState(null);

    const move = (index, delta) => {
        const ids = steps.map((step) => step.Id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + delta, 0, moved);
        onMove(status.id, ids);
    };

    const countLabel = steps.length === 1 ? '1 etapa' : `${steps.length} etapas`;

    return (
        <section className={styles.card}>
            <header className={styles.cardHeader}>
                <div className={styles.cardTitleRow}>
                    <h3 className={styles.cardTitle}>{status.label}</h3>
                    <span className={styles.cardTitleSide}>
                        {!loading && steps.length > 0 && <span className={styles.count}>{countLabel}</span>}
                        <ColorPicker
                            label={`Cor de ${status.label}`}
                            value={statusColor}
                            defaultColors={PALETTE[DEFAULT_STATUS_PALETTE[status.id]]}
                            disabled={!canManage || busy}
                            onChange={(key) => onStatusColor(status.id, key)}
                        />
                    </span>
                </div>
                <span className={styles.cardHint}>{status.hint}</span>
            </header>

            {loading ? (
                <div className={styles.list}>
                    <Skeleton variant="rounded" height={36} />
                </div>
            ) : (
                <ul className={styles.list}>
                    {steps.length === 0 && inactiveSteps.length === 0 && (
                        <li className={styles.empty}>Nenhuma etapa neste status.</li>
                    )}
                    {steps.map((step, index) => (
                        editingId === step.Id ? (
                            <li key={step.Id} className={styles.rowEditing}>
                                <StepForm
                                    statusId={status.id}
                                    initialName={step.Name}
                                    initialRequired={step.IsRequired}
                                    initialColor={step.Color || null}
                                    busy={busy}
                                    onSubmit={(data, done) => onSave({ ...data, id: step.Id }, (ok) => {
                                        if (ok) setEditingId(null);
                                        done(ok);
                                    })}
                                    onCancel={() => setEditingId(null)}
                                />
                            </li>
                        ) : (
                            <li key={step.Id} className={styles.row}>
                                <span className={styles.order}>{index + 1}</span>
                                <span className={styles.nameCell}>
                                    <span className={styles.name}>
                                        <StepColorDot colorKey={step.Color} />
                                        {step.Name}
                                    </span>
                                    <RequiredTag required={step.IsRequired} />
                                </span>
                                {canManage && (
                                    <span className={styles.actions}>
                                        <RowAction title="Subir" Icon={ArrowUpwardRoundedIcon} disabled={busy || index === 0} onClick={() => move(index, -1)} />
                                        <RowAction title="Descer" Icon={ArrowDownwardRoundedIcon} disabled={busy || index === steps.length - 1} onClick={() => move(index, 1)} />
                                        <RowAction title="Editar etapa" Icon={EditOutlinedIcon} disabled={busy} onClick={() => setEditingId(step.Id)} />
                                        <RowAction title="Desativar etapa" Icon={PowerSettingsNewOutlinedIcon} tone="warning" disabled={busy} onClick={() => onDeactivate(step)} />
                                    </span>
                                )}
                            </li>
                        )
                    ))}
                    {inactiveSteps.map((step) => (
                        <li key={step.Id} className={`${styles.row} ${styles.muted}`}>
                            <span className={styles.order} />
                            <span className={styles.nameCell}>
                                <span className={styles.name}>{step.Name}</span>
                                <span className={`${styles.tag} ${styles.tagOptional}`}>Inativa</span>
                            </span>
                            {canManage && (
                                <span className={styles.actions}>
                                    <RowAction title="Reativar etapa" Icon={RestoreOutlinedIcon} tone="positive" disabled={busy} onClick={() => onRestore(step)} />
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {canManage && !loading && (
                <div className={styles.addRow}>
                    <StepForm
                        statusId={status.id}
                        busy={busy}
                        placeholder="Nova etapa"
                        onSubmit={(data, done) => onSave({ ...data, status: status.id }, done)}
                    />
                </div>
            )}
        </section>
    );
}

export default StepStatusCard;
